import React, { useState } from 'react'
import {
  AppBar,
  Box,
  CircularProgress,
  Dialog,
  Divider,
  IconButton,
  Toolbar,
  Typography
} from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import DescriptionIcon from '@material-ui/icons/Description'
import PrintIcon from '@material-ui/icons/Print'
import { makeStyles } from '@material-ui/core/styles'

const useStyles = makeStyles((theme) => ({
  appBar: {
    position: 'relative',
    '@media print': { display: 'none' }
  },
  title: { flex: 1, marginLeft: theme.spacing(2) },
  content: {
    padding: theme.spacing(4),
    maxWidth: 900,
    margin: '0 auto',
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    '@media print': {
      padding: 0,
      maxWidth: '100%'
    }
  },
  coverPage: {
    textAlign: 'center',
    paddingTop: theme.spacing(8),
    paddingBottom: theme.spacing(8),
    pageBreakAfter: 'always',
    '@media print': {
      paddingTop: 120,
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center'
    }
  },
  coverTitle: {
    fontSize: 36,
    fontWeight: 700,
    color: theme.palette.primary.main,
    marginBottom: theme.spacing(4),
    letterSpacing: 2,
    textTransform: 'uppercase'
  },
  coverMeta: {
    fontSize: 16,
    marginBottom: theme.spacing(1),
    color: theme.palette.text.secondary
  },
  coverMetaValue: {
    fontSize: 18,
    fontWeight: 500,
    marginBottom: theme.spacing(3),
    color: theme.palette.text.primary
  },
  tocSection: {
    paddingBottom: theme.spacing(4),
    pageBreakAfter: 'always'
  },
  tocTitle: {
    fontSize: 24,
    fontWeight: 600,
    marginBottom: theme.spacing(3)
  },
  tocItem: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: theme.spacing(0.5, 0),
    borderBottom: '1px dotted #ccc',
    cursor: 'pointer',
    '&:hover': { backgroundColor: '#f5f5f5' }
  },
  itemSection: {
    pageBreakBefore: 'always',
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(3)
  },
  itemHeader: {
    display: 'flex',
    alignItems: 'baseline',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2)
  },
  itemNumber: {
    fontSize: 14,
    fontWeight: 700,
    color: '#fff',
    backgroundColor: theme.palette.primary.main,
    borderRadius: '50%',
    width: 32,
    height: 32,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0
  },
  itemName: {
    fontSize: 22,
    fontWeight: 600
  },
  specGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: '#f9f9f9',
    borderRadius: 8
  },
  specLabel: {
    fontSize: 11,
    fontWeight: 600,
    color: theme.palette.text.secondary,
    textTransform: 'uppercase',
    letterSpacing: 0.5
  },
  specValue: {
    fontSize: 14,
    fontWeight: 400
  },
  cutsheetContainer: {
    marginTop: theme.spacing(2),
    textAlign: 'center',
    position: 'relative',
    minHeight: 200
  },
  cutsheetImage: {
    maxWidth: '100%',
    border: '1px solid #ddd',
    borderRadius: 4,
    '@media print': {
      maxHeight: '60vh',
      objectFit: 'contain'
    }
  },
  notes: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(1.5),
    backgroundColor: '#fff9c4',
    borderRadius: 4,
    borderLeft: '4px solid #fbc02d',
    fontSize: 13
  },
  docCardsRow: {
    display: 'flex',
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
    flexWrap: 'wrap'
  },
  docCard: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.5),
    border: '1px solid #ddd',
    borderRadius: 8,
    textDecoration: 'none',
    color: 'inherit',
    transition: 'box-shadow 0.15s',
    '&:hover': {
      boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
      borderColor: theme.palette.primary.main
    }
  },
  docThumb: {
    width: 48,
    height: 62,
    backgroundColor: '#f5f5f5',
    borderRadius: 4,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '1px solid #eee',
    flexShrink: 0
  },
  docLabel: {
    fontWeight: 600,
    fontSize: 14
  },
  docHint: {
    fontSize: 11,
    color: theme.palette.text.secondary
  }
}))

const CutsheetImage = ({ vendorCatalogId, pageNumber, productName, classes }) => {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  if (!vendorCatalogId || !pageNumber) return null

  return (
    <div className={classes.cutsheetContainer}>
      {loading && !error && (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
          <CircularProgress size={24} />
          <Typography variant="caption" color="textSecondary" style={{ marginLeft: 8 }}>
            Loading cutsheet...
          </Typography>
        </Box>
      )}
      {!error && (
        <img
          className={classes.cutsheetImage}
          src={`/api/vendor-catalogs/${vendorCatalogId}/page-image/${pageNumber}`}
          alt={`Cutsheet - ${productName}`}
          onLoad={() => setLoading(false)}
          onError={() => { setLoading(false); setError(true) }}
          style={loading ? { display: 'none' } : {}}
        />
      )}
      {error && (
        <Typography color="textSecondary" variant="body2" style={{ padding: 20 }}>
          Cutsheet image unavailable
        </Typography>
      )}
    </div>
  )
}

const SubmittalPreview = ({ open, onClose, submittal }) => {
  const classes = useStyles()

  const items = submittal.items || []
  const today = new Date().toLocaleDateString('en-US', {
    year: 'numeric', month: 'long', day: 'numeric'
  })

  const scrollToItem = (idx) => {
    const el = document.getElementById(`submittal-item-${idx}`)
    if (el) el.scrollIntoView({ behavior: 'smooth' })
  }

  return (
    <Dialog open={open} onClose={onClose} fullScreen>
      <AppBar className={classes.appBar}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={onClose}>
            <CloseIcon />
          </IconButton>
          <Typography variant="h6" className={classes.title}>
            Submittal Preview
          </Typography>
          <IconButton color="inherit" onClick={() => window.print()}>
            <PrintIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <div className={classes.content}>
        {/* Cover Page */}
        <div className={classes.coverPage}>
          <Typography className={classes.coverTitle}>
            Material Submittal
          </Typography>
          <Divider style={{ marginBottom: 32 }} />

          {submittal.submittalNumber && (
            <>
              <Typography className={classes.coverMeta}>Submittal No.</Typography>
              <Typography className={classes.coverMetaValue}>{submittal.submittalNumber}</Typography>
            </>
          )}

          <Typography className={classes.coverMeta}>Date</Typography>
          <Typography className={classes.coverMetaValue}>{today}</Typography>

          <Typography className={classes.coverMeta}>Project Name</Typography>
          <Typography className={classes.coverMetaValue}>{submittal.name}</Typography>

          {submittal.projectAddress && (
            <>
              <Typography className={classes.coverMeta}>Location</Typography>
              <Typography className={classes.coverMetaValue}>{submittal.projectAddress}</Typography>
            </>
          )}

          {submittal.generalContractor && (
            <>
              <Typography className={classes.coverMeta}>General Contractor</Typography>
              <Typography className={classes.coverMetaValue}>{submittal.generalContractor}</Typography>
            </>
          )}

          {submittal.architect && (
            <>
              <Typography className={classes.coverMeta}>Architect</Typography>
              <Typography className={classes.coverMetaValue}>{submittal.architect}</Typography>
            </>
          )}

          <Typography className={classes.coverMeta}>Items</Typography>
          <Typography className={classes.coverMetaValue}>{items.length} products</Typography>
        </div>

        {/* Table of Contents */}
        <div className={classes.tocSection}>
          <Typography className={classes.tocTitle}>Table of Contents</Typography>
          {items.map((item, idx) => (
            <div
              key={item._id}
              className={classes.tocItem}
              onClick={() => scrollToItem(idx)}
            >
              <Typography variant="body2">
                {idx + 1}. {item.productName}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {item.manufacturer || '—'} | {item.vendorItemNumber || item.manufacturerPartNumber || '—'}
              </Typography>
            </div>
          ))}
        </div>

        {/* Item Sections */}
        {items.map((item, idx) => (
          <div key={item._id} id={`submittal-item-${idx}`} className={classes.itemSection}>
            <div className={classes.itemHeader}>
              <div className={classes.itemNumber}>{idx + 1}</div>
              <Typography className={classes.itemName}>{item.productName}</Typography>
            </div>

            <div className={classes.specGrid}>
              <div>
                <Typography className={classes.specLabel}>Manufacturer</Typography>
                <Typography className={classes.specValue}>{item.manufacturer || '—'}</Typography>
              </div>
              <div>
                <Typography className={classes.specLabel}>Part Number</Typography>
                <Typography className={classes.specValue}>
                  {item.manufacturerPartNumber || item.vendorItemNumber || '—'}
                </Typography>
              </div>
              <div>
                <Typography className={classes.specLabel}>SKU / Item #</Typography>
                <Typography className={classes.specValue}>{item.vendorItemNumber || '—'}</Typography>
              </div>
              <div>
                <Typography className={classes.specLabel}>Category</Typography>
                <Typography className={classes.specValue}>{item.category || '—'}</Typography>
              </div>
              {item.quantity > 0 && (
                <div>
                  <Typography className={classes.specLabel}>Quantity</Typography>
                  <Typography className={classes.specValue}>{item.quantity} {item.unit}</Typography>
                </div>
              )}
              <div>
                <Typography className={classes.specLabel}>Status</Typography>
                <Typography className={classes.specValue} style={{ textTransform: 'capitalize' }}>
                  {item.approvalStatus || 'Pending'}
                </Typography>
              </div>
            </div>

            {item.shortDescription && (
              <Box mb={1}>
                <Typography variant="body2"><strong>Description:</strong> {item.shortDescription}</Typography>
              </Box>
            )}

            {item.rawSpecText && (
              <Box mb={1}>
                <Typography variant="body2" style={{ whiteSpace: 'pre-wrap', fontSize: 13, color: '#555' }}>
                  {item.rawSpecText}
                </Typography>
              </Box>
            )}

            {item.specOverrides && (
              <div className={classes.notes}>
                <strong>Spec Notes:</strong> {item.specOverrides}
              </div>
            )}

            {item.notes && (
              <div className={classes.notes}>
                <strong>Notes:</strong> {item.notes}
              </div>
            )}

            {/* API product image (takes priority over catalog cutsheet) */}
            {item.productImageUrl ? (
              <div className={classes.cutsheetContainer}>
                <img
                  src={item.productImageUrl}
                  alt={item.productName}
                  className={classes.cutsheetImage}
                  style={{ maxWidth: '100%', maxHeight: 400, objectFit: 'contain' }}
                />
              </div>
            ) : (
              /* Cutsheet: catalog page image */
              <CutsheetImage
                vendorCatalogId={item.vendorCatalogId}
                pageNumber={item.pageNumber}
                productName={item.productName}
                classes={classes}
              />
            )}

            {/* Structured product specs (API-sourced) */}
            {item.productSpecifications && item.productSpecifications.length > 0 && (
              <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 8 }}>
                <tbody>
                  {item.productSpecifications.map((spec, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid #eee' }}>
                      <td style={{ padding: '4px 8px', fontWeight: 500, width: '40%' }}>{spec.label}</td>
                      <td style={{ padding: '4px 8px' }}>{spec.value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {item.documentUrls && item.documentUrls.length > 0 && (
              <div className={classes.docCardsRow}>
                {item.documentUrls.map((doc, i) => (
                  <a
                    key={i}
                    href={doc.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={classes.docCard}
                    onClick={(e) => {
                      e.preventDefault()
                      window.open(doc.url, '_blank', 'noopener,noreferrer')
                    }}
                  >
                    <div className={classes.docThumb}>
                      <DescriptionIcon style={{ fontSize: 28, color: doc.label === 'SDS' ? '#d32f2f' : '#1976d2' }} />
                    </div>
                    <div>
                      <Typography className={classes.docLabel}>{doc.label || 'Document'}</Typography>
                      <Typography className={classes.docHint}>View PDF</Typography>
                    </div>
                  </a>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </Dialog>
  )
}

export default SubmittalPreview
